import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, Stack, Link, CircularProgress,
} from '@mui/material';
import { ArrowBack, MarkEmailRead } from '@mui/icons-material';
import { authAPI } from '../services/api';
import { AuthShell, AuthBrand, AuthCard } from './Login';

const PRIMARY   = '#1a1a2ed4';
const SURFACE   = '#FFFFFF';
const TEXT_DARK = '#1A1A2E';
const TEXT_MID  = '#6B7280';
const DIVIDER   = '#EEEEF5';
const BG        = '#F4F4F8';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    bgcolor: BG,
    fontSize: '0.875rem',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: DIVIDER },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY, borderWidth: '2px' },
  },
};

export default function ForgotPassword() {
  const [email, setEmail]     = useState('');
  const [error, setError]     = useState('');
  const [sent, setSent]       = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <AuthBrand />

      {sent ? (
        <Box
          sx={{
            bgcolor: SURFACE, border: `1px solid ${DIVIDER}`,
            borderRadius: 3, p: 3, textAlign: 'center',
            boxShadow: '0 4px 16px rgba(108,99,255,0.06)',
          }}
        >
          <Box
            sx={{
              width: 52, height: 52, borderRadius: '50%',
              bgcolor: '#4CAF82',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 2,
              boxShadow: '0 4px 12px rgba(76,175,130,0.3)',
            }}
          >
            <MarkEmailRead sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Typography fontWeight={700} sx={{ mb: 1, color: TEXT_DARK, fontSize: '1rem' }}>
            Check your inbox
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: TEXT_MID, fontSize: '0.8125rem', lineHeight: 1.6 }}>
            If <strong style={{ color: TEXT_DARK }}>{email}</strong> is registered, a reset link is on its way.
            Check your spam folder if it doesn't arrive.
          </Typography>
          <Button
            fullWidth variant="contained"
            onClick={() => navigate('/login')}
            sx={{
              py: 1.25, fontWeight: 700, borderRadius: '12px',
              bgcolor: PRIMARY,
              boxShadow: '0 4px 16px rgba(108,99,255,0.35)',
              '&:hover': { bgcolor: '#5850E6', boxShadow: '0 6px 20px rgba(108,99,255,0.45)' },
            }}
          >
            Back to sign in
          </Button>
        </Box>
      ) : (
        <AuthCard
          title="Reset your password"
          subtitle="Enter your email and we'll send you a link to choose a new password."
        >
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5, py: 0.75, borderRadius: 2, fontSize: '0.8125rem',
                bgcolor: 'rgba(255,82,82,0.06)', border: '1px solid rgba(255,82,82,0.2)',
                color: '#FF5252', '& .MuiAlert-icon': { color: '#FF5252', fontSize: 16 },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, fontSize: '0.8125rem', color: TEXT_DARK }}>
                  Email address
                </Typography>
                <TextField
                  required fullWidth autoFocus type="email" size="small"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  sx={fieldSx}
                />
              </Box>

              <Button
                type="submit" fullWidth variant="contained"
                disabled={loading}
                sx={{
                  py: 1.25, fontWeight: 700, borderRadius: '12px',
                  bgcolor: PRIMARY,
                  boxShadow: '0 4px 16px rgba(108,99,255,0.35)',
                  '&:hover': { bgcolor: '#5850E6', boxShadow: '0 6px 20px rgba(108,99,255,0.45)' },
                }}
              >
                {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Send reset link'}
              </Button>
            </Stack>
          </Box>
        </AuthCard>
      )}

      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 2.5 }}>
        <ArrowBack sx={{ fontSize: 13, color: TEXT_MID }} />
        <Link
          component="button" underline="none"
          onClick={() => navigate('/login')}
          sx={{ fontSize: '0.8125rem', color: PRIMARY, fontWeight: 600, '&:hover': { color: '#5850E6' } }}
        >
          Back to sign in
        </Link>
      </Stack>
    </AuthShell>
  );
}
